import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { BackendUserService, NotificationService, QuoteOrderSampleService } from '@coreapiservices';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { map } from 'rxjs/operators';
import Quill from 'quill';
import { AlertService, AuthService, SubscribersService } from '@coreservices';
import { Constants } from 'src/app/admin-core/constants';
import { AdminUserModel } from 'src/app/admin-core/models/admin_user.model';
import { QuoteService } from 'src/app/admin-core/apiservices/quote.service';
import {RequestParamModel} from "@coremodels";
import { stripInlineHandlers } from '../../../admin-core/utils/strip-inline-handlers';


@Component({
  standalone: false,
  selector: 'app-add-comment',
  templateUrl: './add-comment.component.html',
  styleUrls: ['./add-comment.component.css']
})
export class AddCommentComponent implements OnInit {

  comment: any;
  addCommentStockAllocation: boolean = false;
  @Input() commentModel: any;
  @ViewChild('editorContainer') editorContainer: ElementRef;
  selectedUsers = [];
  removedUsers = [];
  users: { value: string, id: number }[] = [];
  submitted: boolean = false;
  unReadNotificationCount = 0;
  quoteProductExistingNotifications = [];
  quillEditor: Quill;
  isQuillEmptyError = false;
  loginUserId: any;
  profile: AdminUserModel;
  isLoading: boolean = false;

  constructor(
    public ngbActiveModal: NgbActiveModal,
    private usersService: BackendUserService,
    private orderService: QuoteOrderSampleService,
    private alertService: AlertService,
    private notificationService: NotificationService,
    private subscribersService: SubscribersService,
    private quoteService: QuoteService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.getBackendUserList();
    let userProfile = JSON.stringify(this.authService.getAdminUser());
    this.profile = JSON.parse(userProfile);
    this.loginUserId = this.profile.id;
  }

  getBackendUserList() {
    let requestParamModel = new RequestParamModel();
    requestParamModel.pageSize = 'all';
    this.usersService.getBackEndUser(requestParamModel).pipe(map((dataList: any) => dataList.data.map((item: any) => ({
      value: item.first_name + ' ' + item.last_name,
      id: item.id
    })))).subscribe((users: { value: string, id: number }[]) => {
      this.users = users;
      this.initializeQuillEditor();
    });
  }

  initializeQuillEditor() {
    if (!this.editorContainer) return;

    this.quillEditor = new Quill(this.editorContainer.nativeElement, {
      modules: {
        toolbar: false,
        mention: {
          allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
          mentionDenotationChars: ["@"],
          source: (searchTerm, renderList, mentionChar) => {
            if (!this.quillEditor.isEnabled()) return;
            if (mentionChar === "@") {
              const matches = this.users.filter(user =>
                user.value.toLowerCase().includes(searchTerm.toLowerCase())
              );
              renderList(matches, searchTerm);
            }
          },
          onSelect: (item, insertItem) => {
            const user = this.users.find(u => u.value === item.value);
            if (user) {
              this.selectedUsers.push(user);
              insertItem(item);
            }
          }
        }
      },
      placeholder: 'Add Note',
      theme: 'snow'
    });

    if (this.commentModel?.note) {
      this.quillEditor.root.innerHTML = stripInlineHandlers(this.commentModel?.note);
      const editorContent = this.getContentWithMentions();
      this.updateSelectedUsers(editorContent);
    }

    this.quillEditor.on('text-change', () => {
      const editorContent = this.getContentWithMentions();
      this.comment = this.quillEditor.root.innerHTML;
      const trimmedValue = this.removeSpaces(this.comment);
      this.isQuillEmptyError = !trimmedValue ? true : false;
      this.updateSelectedUsers(editorContent);
    });
  }

  getContentWithMentions() {
    const delta = this.quillEditor.getContents();
    let content = '';

    delta.ops.forEach(op => {
      if (typeof op.insert === 'string') {
        content += op.insert;
      } else if (op?.insert?.mention) {
        content += `{{mention:${(op?.insert?.mention as any).value}}} `;
      }
    });
    return content.trim();
  }

  updateSelectedUsers(editorContent: any) {
    const mentionRegex = /{{mention:([\w\s]+?)}}/g;
    const mentionedUserValues = [...editorContent.matchAll(mentionRegex)].map(match => match[1].trim());

    const usersToRemove = this.selectedUsers.filter(user => !mentionedUserValues.includes(user.value));

    this.selectedUsers = this.users.filter(user => mentionedUserValues.includes(user.value));

    usersToRemove.forEach(user => {
      if (!this.removedUsers.some(r => r.id === user.id)) {
        this.removedUsers.push({ id: user.id, name: user.value });
      }
    });

    this.removedUsers = this.removedUsers.filter(removedUser => {
      return !this.selectedUsers.some(selectedUser => selectedUser.id === removedUser.id);
    });
  }

  removeSpaces(input: string): string {
    if (!input) return '';
    let noHtml = input.replace(/<[^>]*>/g, '');
    return noHtml.replace(/\s+/g, '');
  }

  sendNotification(response: any) {

   if (Array.isArray(this.selectedUsers) && this.selectedUsers.length > 0) {
      const editorContent = this.getContentWithMentions();
      const mentionRegex = /{{mention:([\w\s]+?)}}/g;

      // Remove mentions from the original note to get the message part
      let messagePart = editorContent.replace(mentionRegex, '').trim();

      // Iterate over selected users to send notifications
      this.selectedUsers.forEach((user: any) => {
        const mention = `@${user.value}`;

        // Construct the message string for this user
        const notificationMessage = `<span class="mention">${mention}</span> ${messagePart}`;

        // Prepare the request model for sending notification
        const requestModel: any = {
          message: notificationMessage,
          quote_product_id: response.id, // Adjust as per your response structure
          from_user_id: this.loginUserId,
          type: Constants.QUOTE_PRODUCT_NOTIFICATION,
          to_user_id: user.id  // Assuming user.id corresponds to to_user_id
        };

        // Send notification with updated message containing mentions
        this.notificationService.sendNotification(requestModel).subscribe((notificationResponse) => {
          this.subscribersService.updateUnreadNotification(this.unReadNotificationCount.toString());
        });
      });

      // Delete notifications for removed users
      if (Array.isArray(this.removedUsers) && this.removedUsers.length > 0) {
        this.removedUsers.forEach((removedUser: any) => {
          // Find the object in the array that matches all criteria
          const existingNotificationObject = this.commentModel?.notifications?.find(obj =>
            obj.note_id === response.id &&
            obj.from_user_id === response.backend_user_id &&
            obj.to_user_id === removedUser.id
          );
          const id = existingNotificationObject ? existingNotificationObject.id : null;

          this.notificationService.deleteNotification(id).subscribe((deleteResponse) => {
            // Handle delete response if needed
          });
        });
      }
    }
  }

  submit() {
    this.submitted = true;
     this.isLoading = true;
    const trimmedValue = this.removeSpaces(this.comment);
    if (!trimmedValue) {
      this.isQuillEmptyError = true;
      return;
    } else this.isQuillEmptyError = false;
    this.quoteService.updateQuoteProductForCreateClone({ note: this.comment, only_change_note: true }, this.commentModel.id).subscribe((res: any) => {
      this.alertService.success(this.commentModel.note ? Constants.INTERNAL_NOTE_UPDATE : Constants.INTERNAL_NOTE_ADDED);
      const note = res.note;
      if (note.includes('@')) {
        this.sendNotification(res)
      }
      this.commentModel.note = note;
      this.comment = null;
       this.isLoading = false;
      this.ngbActiveModal.close(true);
    });
  }

}
